import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
/**
 * Shown the first time a player opens a battle they haven't picked a deck
 * for themselves — most notably the opponent, whose deck slot
 * CreateBattleController auto-locks from their most-recently-saved complete
 * deck rather than asking (see SelectBattleDeckController's class doc).
 * Same auto-open-on-entering-the-battle pattern as RespondDrawModal.
 */
export default class SelectBattleDeckModal extends Modal {
  loading = false;
  loadingDecks = true;
  error = null;
  readyDeckSlots = [];
  selectedDeckSlotId = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.loadDeckSlots();
  }
  async loadDeckSlots() {
    this.loadingDecks = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots`
      });
      const slots = response?.slots ?? [];
      this.readyDeckSlots = slots.filter(s => s.cardCount === 20);
      const current = this.attrs.currentDeckSlotId;
      this.selectedDeckSlotId = current !== null && this.readyDeckSlots.some(s => s.id === current) ? current : this.readyDeckSlots[0]?.id ?? null;
    } catch {
      this.readyDeckSlots = [];
      this.selectedDeckSlotId = null;
    } finally {
      this.loadingDecks = false;
      m.redraw();
    }
  }
  className() {
    return 'ArenaSelectDeckModal Modal--small';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-layer-group"
    }), " ", app.translator.trans('forumaker-arena.forum.select_deck_modal.title'));
  }
  content() {
    const hasDeck = !this.loadingDecks && this.readyDeckSlots.length > 0;
    // random_cards: the opponent's modifier choice already decided every
    // card in play for this battle (see RandomDeckBuilder) — picking a deck
    // slot here wouldn't do anything, so the copy/button say so instead of
    // pretending the choice matters.
    const isRandomCards = this.attrs.battle.modifier() === 'random_cards';
    return m("div", {
      className: "Modal-body"
    }, m("p", {
      className: "ArenaSelectDeckModal-text"
    }, app.translator.trans(isRandomCards ? 'forumaker-arena.forum.select_deck_modal.text_random_cards' : 'forumaker-arena.forum.select_deck_modal.text')), this.error && m("div", {
      className: "Alert Alert--error",
      style: {
        marginBottom: '12px'
      }
    }, m("i", {
      className: "fas fa-exclamation-circle"
    }), " ", this.error), !hasDeck && !this.loadingDecks && m("div", {
      className: "Alert Alert--warning",
      style: {
        marginBottom: '12px'
      }
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), ' ', app.translator.trans('forumaker-arena.forum.challenge_modal.no_deck'), ' ', m("a", {
      className: "ArenaSelectDeckModal-buildDeckLink",
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.hide();
        m.route.set(app.route('user.arena', {
          username: app.session.user?.username() ?? ''
        }));
      }
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.build_deck'))), hasDeck && !isRandomCards && m("div", {
      className: "Form-group ArenaSelectDeckModal-deckWrap"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.challenge_modal.deck_label')), m("select", {
      className: "FormControl",
      value: this.selectedDeckSlotId ?? '',
      onchange: e => {
        this.selectedDeckSlotId = parseInt(e.target.value, 10);
      }
    }, this.readyDeckSlots.map(slot => m("option", {
      key: slot.id,
      value: slot.id
    }, slot.name)))), m("div", {
      className: "Form-group ArenaSelectDeckModal-submitGroup"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      loading: this.loading,
      disabled: !hasDeck || this.selectedDeckSlotId == null || this.loading,
      onclick: () => this.submit()
    }, app.translator.trans(isRandomCards ? 'forumaker-arena.forum.select_deck_modal.start_btn' : 'forumaker-arena.forum.select_deck_modal.choose_btn'))));
  }
  async submit() {
    if (this.selectedDeckSlotId == null) return;
    this.loading = true;
    this.error = null;
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/battles/${this.attrs.battle.id()}/select-deck`,
        body: {
          deckSlotId: this.selectedDeckSlotId
        },
        errorHandler: () => {} // see ArenaProfilePage.tsx saveDeck() — this.error already renders its own inline banner
      });
      this.hide();
      this.attrs.onSelected?.(this.selectedDeckSlotId);
    } catch (e) {
      const body = e?.response ?? e?.response?.body ?? e?.responseJSON ?? {};
      // No dedicated locale key for this: the server always replies with its
      // own localized {error} on the validation paths that can actually
      // trigger here (deck incomplete, already played), so this fallback is
      // only ever reached on an unexpected/network failure — Flarum core's
      // generic error string covers that.
      this.error = body?.error ?? body?.errors?.[0]?.detail ?? app.translator.trans('core.lib.error.generic_message');
      this.loading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/SelectBattleDeckModal', SelectBattleDeckModal);